import React, { Fragment, useRef, useState } from 'react';
import {
    Box,
    Chip,
    Divider,
    MenuItem,
    MenuList,
    Paper,
    Popover,
    Typography,
} from '@material-ui/core';
import { Check, ChevronDown } from 'react-feather';
import { useTranslation } from 'react-i18next';
import type { TConversationListFilter } from '../../constants/conversationStatus';
import { useTheme } from '@material-ui/core/styles';
import { shouldShowOmnichannelAreaFilter } from '../../utils/omnichannelAreaFilter';
import { getAreaFilterSection } from './getAreaFilterSection';
import type { TSettingsMenuSection } from '../../types';

export const ConversationSettings = ({
    listFilter,
    onListFilterChange,
    tagsView,
    onViewChange,
    availableAreas = [],
    selectedArea = '',
    onAreaFilterChange,
}: {
    listFilter: TConversationListFilter;
    onListFilterChange: (filter: TConversationListFilter) => void;
    tagsView: 'none' | 'labels' | 'badges';
    onViewChange: (view: 'none' | 'labels' | 'badges') => void;
    availableAreas?: string[];
    selectedArea?: string;
    onAreaFilterChange?: (area: string) => void;
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const handleToggleOpen = () => setOpen(!open);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const theme = useTheme();

    const SETTINGS_MENU: TSettingsMenuSection[] = [
        {
            title: t('Chat.ConversationsType'),
            items: [
                {
                    label: t('Chat.IncomeConversations'),
                    onClick: () => onListFilterChange('active'),
                    isActive: listFilter === 'active',
                },
                {
                    label: t('Chat.ArchivedConversations'),
                    onClick: () => onListFilterChange('archived'),
                    isActive: listFilter === 'archived',
                },
            ],
        },
        ...getAreaFilterSection({
            show: shouldShowOmnichannelAreaFilter(availableAreas),
            areas: availableAreas,
            selectedArea,
            onAreaChange: onAreaFilterChange,
            t,
        }),
        { divider: true },
        {
            title: t('Chat.TagsView'),
            items: [
                {
                    label: t('Chat.TagsNoShow'),
                    onClick: () => onViewChange('none'),
                    isActive: tagsView === 'none',
                },
                {
                    label: t('Chat.TagsShowLabels'),
                    onClick: () => onViewChange('labels'),
                    isActive: tagsView === 'labels',
                },
                {
                    label: t('Chat.TagsShowColors'),
                    onClick: () => onViewChange('badges'),
                    isActive: tagsView === 'badges',
                },
            ],
        },
    ];

    return (
        <Fragment>
            <Popover
                open={open}
                onClose={handleToggleOpen}
                anchorEl={buttonRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper>
                    <MenuList>
                        {SETTINGS_MENU.map((setting, index) =>
                            setting.divider ? (
                                <Divider key={`divider-${index}`} />
                            ) : (
                                <Fragment key={setting.title}>
                                    <MenuItem>
                                        <Typography variant="subtitle2">
                                            {String(setting.title)}
                                        </Typography>
                                    </MenuItem>

                                    {setting.items.map(
                                        ({ isActive, label, onClick }) => (
                                            <MenuItem
                                                key={label}
                                                onClick={onClick}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                }}
                                            >
                                                {label}
                                                {isActive && (
                                                    <Check
                                                        size={20}
                                                        color={
                                                            theme.palette
                                                                .primary.main
                                                        }
                                                    />
                                                )}
                                            </MenuItem>
                                        )
                                    )}
                                </Fragment>
                            )
                        )}
                    </MenuList>
                </Paper>
            </Popover>
            <Box>
                <Chip
                    variant={'outlined'}
                    onClick={handleToggleOpen}
                    ref={buttonRef as any}
                    label={
                        <Box display="flex" alignItems="center" gridGap={5}>
                            {t('Settings.Settings')}
                            <ChevronDown style={{ width: 18, height: 18 }} />
                        </Box>
                    }
                />
            </Box>
        </Fragment>
    );
};
